import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, AuthResponse } from '../services/authService';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  store: any;
  login: (shop: string, authType?: 'oauth' | 'embedded', embeddedParams?: any) => Promise<AuthResponse>;
  logout: () => void;
  validateToken: (token: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [store, setStore] = useState<any>(null);

  // Initialize authentication state
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      setIsLoading(true);

      // Preserve URL parameters for later use
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.toString()) {
        sessionStorage.setItem('login_url_params', urlParams.toString());
      }

      // Check for stored authentication data
      const { token, user: storedUser, store: storedStore } = authService.getStoredAuthData();

      if (token && storedUser && storedStore) {
        // Validate stored token
        const isValid = await validateToken(token);
        
        if (isValid) {
          setIsAuthenticated(true);
          setUser(storedUser);
          setStore(storedStore);
        } else {
          // Token is invalid, clear stored data
          authService.clearAuthData();
        }
      }

      // Check for OAuth callback parameters
      if (authService.hasOAuthCallbackParams()) {
        // Handle OAuth callback - redirect to backend
        const backendUrl = import.meta.env.VITE_API_URL || 'https://app.translationflow.microdesk.space';
        const callbackUrl = `${backendUrl}/api/auth/oauth/callback?${window.location.search}`;
        window.location.href = callbackUrl;
        return;
      }

      // Check for token in URL (from OAuth callback)
      const tokenFromUrl = urlParams.get('token');
      
      if (tokenFromUrl) {
        // Validate token from URL
        const isValid = await validateToken(tokenFromUrl);
        
        if (isValid) {
          // Get user and store data from backend
          try {
            const userData = await authService.getCurrentUser();
            setUser(userData.user);
            setStore(userData.store);
            setIsAuthenticated(true);
            
            // Store authentication data
            authService.storeAuthData(tokenFromUrl, userData.user, userData.store);
            
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
          } catch (error) {
            console.error('Failed to get user data:', error);
            authService.clearAuthData();
          }
        } else {
          authService.clearAuthData();
        }
      }

      // Check for automatic authentication in Shopify admin
      if (!isAuthenticated && authService.isInShopifyAdmin()) {
        await handleAutomaticAuth();
      }

    } catch (error) {
      console.error('Authentication initialization error:', error);
      authService.clearAuthData();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutomaticAuth = async () => {
    try {
      const shop = authService.extractShopFromUrl();
      
      if (!shop) {
        console.warn('No shop found in URL for automatic authentication');
        return;
      }

      console.log('Attempting automatic authentication for shop:', shop);

      // Check if we have embedded parameters
      if (authService.hasEmbeddedParams()) {
        console.log('Found embedded parameters, attempting embedded authentication');
        const embeddedParams = authService.getEmbeddedParams();
        const result = await login(shop, 'embedded', embeddedParams);
        
        if (!result.success && result.needsOAuth) {
          console.log('Embedded auth failed, initiating OAuth flow');
          // If embedded auth fails and needs OAuth, handle it gracefully
          // The OAuth URL will be returned and can be opened in a new window
        }
      } else {
        console.log('No embedded parameters, initiating OAuth flow');
        // No embedded parameters, try OAuth flow
        const result = await login(shop, 'oauth');
        
        if (result.needsOAuth && result.oauthUrl) {
          console.log('Opening OAuth URL in new window:', result.oauthUrl);
          window.open(result.oauthUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        }
      }
    } catch (error) {
      console.error('Automatic authentication failed:', error);
    }
  };

  const login = async (shop: string, authType: 'oauth' | 'embedded' = 'oauth', embeddedParams?: any): Promise<AuthResponse> => {
    try {
      setIsLoading(true);

      let response: AuthResponse;

      if (authType === 'embedded' && embeddedParams) {
        response = await authService.handleEmbeddedAuth(shop, embeddedParams);
      } else {
        response = await authService.handleOAuthFlow(shop);
      }

      if (response.success && response.token && response.user && response.store) {
        // Store authentication data
        authService.storeAuthData(response.token, response.user, response.store);
        
        // Update state
        setIsAuthenticated(true);
        setUser(response.user);
        setStore(response.store);

        return response;
      } else if (response.needsOAuth && response.oauthUrl) {
        // Need OAuth flow, open in new window
        console.log('Opening OAuth URL in new window:', response.oauthUrl);
        window.open(response.oauthUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        return response;
      } else {
        // Authentication failed
        console.error('Authentication failed:', response.error || response.message);
        return response;
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'Login failed'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    authService.clearAuthData();
    setIsAuthenticated(false);
    setUser(null);
    setStore(null);
  };

  const validateToken = async (token: string): Promise<boolean> => {
    try {
      const response = await authService.validateToken(token);
      return response.valid;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    store,
    login,
    logout,
    validateToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 